/**
 * Nold Go — Covoiturage : profil + réservations passager + notation.
 * 3 pages exportées :
 *   /covoiturage/profil            → CarpoolProfile      (photo + prénom + phone SMS)
 *   /covoiturage/mes-reservations  → CarpoolMyBookings   (passager)
 *   /covoiturage/noter/:booking_id → CarpoolRate         (post-trajet)
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  User, Phone, Camera, CheckCircle2, Loader2, Star, StarOff, Calendar,
  Clock, ArrowRight, MapPin, X, ShieldCheck, Send, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SEOHead from "../components/SEOHead";
import { CarpoolChatModal } from "./CarpoolLanding";

// ----------------------------------------------------- utils
function Stars({ value = 0, size = 4 }) {
  const n = Math.round(Number(value) || 0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) =>
        s <= n
          ? <Star key={s} className={`w-${size} h-${size} fill-[#F26419] text-[#F26419]`} />
          : <StarOff key={s} className={`w-${size} h-${size} text-[#75807A]/40`} />
      )}
    </span>
  );
}

// ===================================================== PROFILE
export function CarpoolProfile() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/carpool/profile/me").then((r) => {
    setProfile(r.data);
    setFirstName(r.data.first_name || "");
    setPhone(r.data.phone || user?.phone || "");
  }).catch(() => {});

  useEffect(() => { if (user) load(); else nav("/login"); }, [user]);

  const saveName = async () => {
    if (!firstName.trim()) { toast.error("Prénom requis"); return; }
    setBusy(true);
    try {
      await api.post("/carpool/profile", { first_name: firstName.trim() });
      toast.success("Prénom enregistré");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/carpool/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo mise à jour");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setUploading(false); }
  };

  const sendCode = async () => {
    if (!phone.trim()) { toast.error("Numéro requis"); return; }
    try {
      const { data } = await api.post("/auth/phone/send-code", { phone });
      setCodeSent(true);
      toast.success(data.sms_sent ? "Code SMS envoyé" : `Code démo : ${data.demo_code || "voir logs"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const confirmCode = async () => {
    if (!code.trim()) { toast.error("Code requis"); return; }
    setBusy(true);
    try {
      await api.post("/carpool/profile/phone-confirm", { phone, code });
      toast.success("Téléphone vérifié ✓");
      setCode(""); setCodeSent(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!profile) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0]"><Loader2 className="w-6 h-6 animate-spin text-[#75807A]" /></div>;

  const since = profile.since_at ? new Date(profile.since_at).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : null;

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-profile-page">
      <SEOHead title="Mon profil covoiturage — Nold Go" description="Complétez votre profil covoiturage." canonical="/covoiturage/profil" noindex />
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#059669] flex items-center justify-center"><User className="w-7 h-7 text-white" /></div>
          <h1 className="font-head text-3xl font-black text-[#1A1A1A]">Mon profil</h1>
        </div>

        {/* Résumé public visible par les autres */}
        <div className="bg-white rounded-2xl p-5 border border-[#E2DFD8] flex items-center gap-4">
          {profile.photo
            ? <img src={profile.photo} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#E2DFD8]" />
            : <div className="w-20 h-20 rounded-full bg-[#F0EBE1] flex items-center justify-center text-2xl font-black text-[#75807A]">{(firstName || "?")[0].toUpperCase()}</div>}
          <div className="flex-1">
            <div className="text-lg font-black flex items-center gap-2">
              {firstName || "Prénom manquant"}
              {profile.verification_ok && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#059669]/10 text-[#059669] text-[10px] font-bold">
                  <ShieldCheck className="w-3 h-3" /> Vérifié
                </span>
              )}
            </div>
            <div className="text-xs text-[#75807A] flex items-center gap-2 mt-1">
              <Stars value={profile.rating_avg} />
              <span>{profile.rating_avg.toFixed(1)}</span>
              <span>· {profile.rating_count} avis</span>
              <span>· {profile.trips_count} trajet(s)</span>
            </div>
            {since && <div className="text-xs text-[#75807A] mt-1">Sur Nold Go depuis {since}</div>}
          </div>
        </div>

        {/* Photo */}
        <div className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
          <div className="flex items-center gap-2 mb-2">
            <Camera className="w-4 h-4 text-[#75807A]" />
            <div className="font-bold text-sm">Photo de profil <span className="text-[#75807A] font-normal">(fortement recommandée)</span></div>
          </div>
          <input data-testid="profile-photo-input" type="file" accept="image/jpeg,image/png,image/webp"
            onChange={(e) => uploadPhoto(e.target.files?.[0])}
            disabled={uploading} className="text-xs" />
          {uploading && <div className="text-xs text-[#75807A] mt-2 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Envoi…</div>}
        </div>

        {/* Prénom */}
        <div className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-[#75807A]" />
            <div className="font-bold text-sm">Prénom <span className="text-[#DC2626]">*</span></div>
          </div>
          <div className="flex gap-2">
            <input data-testid="profile-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ex : Marie"
              className="flex-1 h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm" />
            <button data-testid="profile-save-name" onClick={saveName} disabled={busy}
              className="px-4 h-11 rounded-xl bg-[#059669] hover:bg-[#047857] text-white text-sm font-bold disabled:opacity-50">
              Enregistrer
            </button>
          </div>
        </div>

        {/* Téléphone SMS */}
        <div className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
          <div className="flex items-center gap-2 mb-2">
            <Phone className="w-4 h-4 text-[#75807A]" />
            <div className="font-bold text-sm">
              Téléphone vérifié SMS <span className="text-[#DC2626]">*</span>
              {profile.phone_verified && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#059669]/10 text-[#059669] text-[10px] font-bold">
                  <CheckCircle2 className="w-3 h-3" /> Vérifié
                </span>
              )}
            </div>
          </div>
          {profile.phone_verified ? (
            <div className="text-xs text-[#3B423E]">Numéro vérifié : <b>{profile.phone}</b>. <button className="text-[#F26419] font-bold underline" onClick={() => { setCodeSent(false); }}>Changer</button></div>
          ) : (
            <>
              <div className="flex gap-2">
                <input data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="+32 4 xx xx xx xx"
                  className="flex-1 h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm" />
                <button data-testid="profile-phone-send" onClick={sendCode}
                  className="px-4 h-11 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white text-sm font-bold inline-flex items-center gap-1">
                  <Send className="w-3 h-3" /> Envoyer un code
                </button>
              </div>
              {codeSent && (
                <div className="flex gap-2 mt-2">
                  <input data-testid="profile-phone-code" value={code} onChange={(e) => setCode(e.target.value)}
                    placeholder="Code SMS reçu (6 chiffres)"
                    className="flex-1 h-11 rounded-xl border border-[#E2DFD8] px-3 text-sm tracking-widest text-center font-bold" />
                  <button data-testid="profile-phone-confirm" onClick={confirmCode} disabled={busy}
                    className="px-4 h-11 rounded-xl bg-[#059669] hover:bg-[#047857] text-white text-sm font-bold disabled:opacity-50">
                    {busy ? "…" : "Vérifier"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {profile.incomplete_reason && (
          <div className="rounded-xl bg-[#F26419]/10 border border-[#F26419]/30 p-3 text-xs text-[#3B423E]">
            ⚠️ {profile.incomplete_reason}
          </div>
        )}
        {!profile.incomplete_reason && (
          <Link to="/covoiturage" data-testid="profile-cta-search"
            className="block text-center rounded-xl bg-[#059669] hover:bg-[#047857] text-white font-bold py-3">
            Profil complet — Rechercher un trajet →
          </Link>
        )}
      </div>
    </div>
  );
}

// ===================================================== MY BOOKINGS (passager)
export function CarpoolMyBookings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [chatBookingId, setChatBookingId] = useState(null);
  const load = () => api.get("/carpool/bookings/mine")
    .then((r) => setData(r.data))
    .catch((e) => {
      setError(e?.response?.data?.detail || "Impossible de charger vos réservations.");
      setData({ items: [] });
    });
  useEffect(() => {
    if (!user) { nav("/login"); return; }
    load();
  }, [user]);

  const cancel = async (id) => {
    if (!window.confirm("Annuler cette réservation ? Remboursement complet (> 24 h avant le départ).")) return;
    try {
      await api.post(`/carpool/bookings/${id}/cancel`);
      toast.success("Réservation annulée — remboursement en cours");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0]"><Loader2 className="w-6 h-6 animate-spin text-[#75807A]" /></div>;
  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-mine-bookings-page">
      <SEOHead title="Mes réservations covoiturage — Nold Go" description="Vos billets covoiturage." canonical="/covoiturage/mes-reservations" noindex />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="font-head text-3xl font-black mb-4">Mes réservations</h1>
        {error && (
          <div className="rounded-2xl bg-[#DC2626]/10 border border-[#DC2626]/30 p-3 text-sm text-[#DC2626] mb-4">
            ⚠️ {error}
          </div>
        )}
        {data.items.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-[#75807A]">
            Aucune réservation pour l'instant. <Link to="/covoiturage" className="text-[#F26419] font-bold underline">Rechercher un trajet →</Link>
          </div>
        ) : data.items.map((b) => {
          const t = b.trip || {};
          const status = b.status;
          const statusCls = status === "paid" ? "bg-[#059669] text-white"
            : status === "pending" ? "bg-[#F26419] text-white"
            : status === "cancelled" ? "bg-[#DC2626]/80 text-white"
            : "bg-[#75807A]/30 text-[#3B423E]";
          return (
            <div key={b.id} data-testid={`my-booking-${b.id}`} className="bg-white rounded-2xl p-4 border border-[#E2DFD8] mb-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 text-lg font-bold">
                    {t.from_city} <ArrowRight className="w-4 h-4 text-[#75807A]" /> {t.to_city}
                    <span className={`ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusCls}`}>{status}</span>
                  </div>
                  <div className="text-xs text-[#75807A] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.date}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time}</span>
                    <span>· {b.seats} place(s) · {b.amount_eur.toFixed(2)} €</span>
                  </div>
                  {t.meeting_point && <div className="text-xs text-[#3B423E] mt-1"><MapPin className="inline w-3 h-3 mr-0.5" /> {t.meeting_point}</div>}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  {status === "paid" && (
                    <button data-testid={`chat-open-${b.id}`} onClick={() => setChatBookingId(b.id)}
                      className="px-3 py-1.5 rounded-full bg-[#059669] hover:bg-[#047857] text-white text-xs font-bold inline-flex items-center gap-1">
                      <MessageCircle className="w-3 h-3" /> Discuter
                    </button>
                  )}
                  {b.can_cancel && (
                    <button data-testid={`cancel-${b.id}`} onClick={() => cancel(b.id)}
                      className="px-3 py-1.5 rounded-full border border-[#DC2626]/40 text-[#DC2626] text-xs font-bold hover:bg-[#DC2626]/5 inline-flex items-center gap-1">
                      <X className="w-3 h-3" /> Annuler
                    </button>
                  )}
                  {status === "paid" && !b.rated && (
                    <Link to={`/covoiturage/noter/${b.id}`} data-testid={`rate-link-${b.id}`}
                      className="px-3 py-1.5 rounded-full bg-[#F26419] text-white text-xs font-bold inline-flex items-center gap-1">
                      <Star className="w-3 h-3" /> Noter
                    </Link>
                  )}
                  {b.rated && (
                    <span className="text-xs text-[#059669] inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Noté</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {chatBookingId && (
          <CarpoolChatModal bookingId={chatBookingId} currentUserId={user?.id}
            onClose={() => setChatBookingId(null)} />
        )}
      </div>
    </div>
  );
}

// ===================================================== RATE booking
export function CarpoolRate() {
  const { booking_id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [booking, setBooking] = useState(null);
  const [existing, setExisting] = useState([]);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    api.get(`/carpool/bookings/${booking_id}`).then(r => setBooking(r.data)).catch(() => {});
    api.get(`/carpool/bookings/${booking_id}/reviews`).then(r => setExisting(r.data.reviews || [])).catch(() => {});
  }, [booking_id, user]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/carpool/bookings/${booking_id}/rate`, { stars, comment: comment.trim() });
      toast.success("Merci pour votre note ⭐");
      nav("/covoiturage/mes-reservations");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (!booking) return <div className="min-h-screen flex items-center justify-center bg-[#FDF8F0]"><Loader2 className="w-6 h-6 animate-spin text-[#75807A]" /></div>;
  const t = booking.trip || {};
  const isPassenger = user?.id === booking.passenger_user_id;
  const direction = isPassenger ? "passenger_to_driver" : "driver_to_passenger";
  const already = existing.find((r) => r.direction === direction);
  const target = isPassenger ? "conducteur" : "passager";

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="carpool-rate-page">
      <SEOHead title="Noter le trajet — Nold Go" description="Notez votre trajet Nold Go." canonical={`/covoiturage/noter/${booking_id}`} noindex />
      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
        <h1 className="font-head text-3xl font-black">Noter le {target}</h1>
        <div className="bg-white rounded-2xl p-4 border border-[#E2DFD8]">
          <div className="flex items-center gap-2 text-lg font-bold">{t.from_city} <ArrowRight className="w-4 h-4 text-[#75807A]" /> {t.to_city}</div>
          <div className="text-xs text-[#75807A] mt-1">{t.date} · {t.time}</div>
        </div>
        {already ? (
          <div className="rounded-2xl bg-[#059669]/10 border border-[#059669]/30 p-4 text-sm">
            <div className="font-bold text-[#059669] inline-flex items-center gap-2 mb-1"><CheckCircle2 className="w-4 h-4" /> Vous avez déjà noté</div>
            <div className="inline-flex items-center gap-2"><Stars value={already.stars} /> · {already.stars}/5</div>
            {already.comment && <div className="text-xs text-[#3B423E] mt-2 italic">« {already.comment} »</div>}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-4 border border-[#E2DFD8] space-y-3">
            <div>
              <div className="text-xs font-bold text-[#75807A] mb-2">Votre note</div>
              <div className="flex gap-1" data-testid="rate-stars">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button key={s} type="button" data-testid={`rate-star-${s}`} onClick={() => setStars(s)}
                    className="p-1">
                    <Star className={`w-8 h-8 ${s <= stars ? "fill-[#F26419] text-[#F26419]" : "text-[#75807A]/30"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-[#75807A] mb-1">Commentaire (optionnel)</div>
              <textarea data-testid="rate-comment" value={comment} onChange={(e) => setComment(e.target.value)}
                rows={3} maxLength={500} placeholder={`Un mot pour aider les futurs ${target}s…`}
                className="w-full rounded-xl border border-[#E2DFD8] p-3 text-sm outline-none focus:border-[#059669]" />
              <div className="text-[10px] text-[#75807A] text-right">{comment.length}/500</div>
            </div>
            <button data-testid="rate-submit" onClick={submit} disabled={busy}
              className="w-full h-11 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-sm disabled:opacity-50">
              {busy ? "Envoi…" : "Envoyer ma note"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Petit composant réutilisable de snapshot conducteur — importable depuis Carpool.jsx.
export function DriverSnapshot({ trip, compact = false }) {
  if (!trip) return null;
  const photo = trip.driver_photo;
  const first = trip.driver_first_name || (trip.driver_name || "").split(" ")[0] || "Conducteur";
  const avg = Number(trip.driver_rating_avg || 0);
  const cnt = Number(trip.driver_rating_count || 0);
  const trips = Number(trip.driver_trips_count || 0);
  const size = compact ? "w-8 h-8" : "w-12 h-12";
  return (
    <div className="flex items-center gap-2" data-testid="driver-snapshot">
      {photo
        ? <img src={photo} alt="" className={`${size} rounded-full object-cover border border-[#E2DFD8]`} />
        : <div className={`${size} rounded-full bg-[#F0EBE1] flex items-center justify-center text-sm font-black text-[#75807A]`}>{first[0]?.toUpperCase()}</div>}
      <div className="min-w-0">
        <div className="text-sm font-bold text-[#1A1A1A] flex items-center gap-1.5 flex-wrap">
          {first}
          {trip.driver_verified && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#059669]/10 text-[#059669] text-[10px] font-bold">
              <ShieldCheck className="w-2.5 h-2.5" /> Vérifié
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#75807A] flex items-center gap-1.5 flex-wrap">
          {cnt > 0
            ? <><Stars value={avg} size={3} /> <span>{avg.toFixed(1)} · {cnt} avis</span></>
            : <span className="italic">Aucun avis pour l'instant</span>}
          {trips > 0 && <span>· {trips} trajet(s)</span>}
        </div>
      </div>
    </div>
  );
}
